import clsx from "clsx";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UtilityStore } from "~/stores/UtilityStore";
import { useTabStore, useHelperStore } from "~/stores";
import { ResultType } from "~/features/Search/Results/types";
import { TabKeys } from "~/stores/tab/tab.state";
import { Region } from "~/providers/LanguageProvider";
import { SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

// Copied and adjusted from /features/SearchV2/SearchTypeTabs.tsx
const SearchTypeTabs = () => {
    const { t } = useTranslation();
    const REGION = import.meta.env.VITE_REGION;

    const dispatchTab = useTabStore(state => state.dispatch);
    const activeTab = useTabStore(state => state.tab);
    const isFetching = useHelperStore(state => state.isLoading);

    const tabs:TabKeys[] = useMemo(() => {
        return REGION === Region.US
            ? [SEARCH_TABS.IMAGES, SEARCH_TABS.KEYWORDS, SEARCH_TABS.REPORTS]
            : [SEARCH_TABS.IMAGES, SEARCH_TABS.KEYWORDS, SEARCH_TABS.REPORTS, SEARCH_TABS.FULL_DOCUMENTS];
    }, []);

    const toggleActiveTabs = async (tab: TabKeys) => {
        dispatchTab({ type: SearchActions.SET_ACTIVETAB_BY_KEY, payload: tab });
        setIsMarketing(false);
    };

    const isActiveTab = (tab: ResultType) => activeTab?.key === tab;
    const setIsMarketing = UtilityStore((state: any) => state.setIsMarketingIntelligence);

    useEffect(() => {
        const tabItems = tabs.map((tab, id) => ({ id, key: tab }));
        dispatchTab({ type: SearchActions.SET_TAB_ITEMS, payload: tabItems });

        //made the default tab selected upon reload manually
        const defaultTab = SEARCH_TABS.IMAGES;
        if (!activeTab) {
             dispatchTab({
                type: SearchActions.SET_ACTIVETAB_BY_KEY,
                payload: defaultTab,
            });
        }
    }, []);

    const renderedTabs = tabs.map((tab: TabKeys, i) => {
        const buttonTabClass = clsx(
            "text-sm text-stone-vibrant h-full",
            isActiveTab(tab)
                ? "border-b-2 border-salmon-primary !text-stone-darkest font-bold"
                : "text-stone-vibrant border-b-2 border-[var(--background-surface-page)]"
        );

        return <button
            key={i}
            onClick={() => toggleActiveTabs(tab)}
            className={buttonTabClass}
            disabled={isFetching}
        >
            {t(`searchResultTypes.${tab}`)}
        </button>
    });

    return (
        <div className="relative h-11">
            <div className="relative h-full flex row gap-6 z-20 justify-center items-center p-1">
                {renderedTabs}
            </div>
            <div className="absolute w-full h-full bg-white opacity-60 rounded-full z-0 top-0" />
        </div>
    );
};

export default SearchTypeTabs;
